import React, { useState, useEffect, useRef } from 'react';
import { geocodingService } from '../services/geocoding';

const SearchBar = ({ onLocationSelect, onClose }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-focus when opened
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    // Debounce search
    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            if (query.length >= 3) {
                setIsLoading(true);
                const locations = await geocodingService.searchLocation(query);
                setResults(locations);
                setIsLoading(false);
                setIsOpen(true);
            } else {
                setResults([]);
                setIsOpen(false);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [query]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                // If clicking outside and onClose exists, trigger it
                if (onClose) onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const handleSelect = (location) => {
        setQuery(location.display_name.split(',')[0]);
        setIsOpen(false);
        onLocationSelect({
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lon),
            name: location.display_name
        });
        if (onClose) onClose();
    };

    return (
        <div ref={wrapperRef} className="absolute top-4 md:top-6 left-1/2 transform -translate-x-1/2 z-[1001] w-[90%] md:w-full max-w-md transition-all duration-300">
            <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className={`w-5 h-5 ${isLoading ? 'text-indigo-600 animate-spin' : 'text-gray-600 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isLoading ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        )}
                    </svg>
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    className="block w-full pl-10 pr-3 py-3 border border-white/40 dark:border-gray-500/30 rounded-2xl leading-5 bg-white/30 dark:bg-gray-800/80 backdrop-blur-xl placeholder-gray-700 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-white/50 dark:focus:bg-gray-800/90 focus:border-white/60 sm:text-sm shadow-lg hover:shadow-xl transition-all duration-300"
                    placeholder="Search for a city or street..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => query.length >= 3 && setIsOpen(true)}
                />

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Results Dropdown */}
                {isOpen && results.length > 0 && (
                    <ul className="absolute z-10 w-full mt-2 bg-white/60 dark:bg-gray-800/90 backdrop-blur-xl shadow-2xl rounded-2xl max-h-60 overflow-auto border border-white/40 dark:border-gray-600/30 py-2 scrollbar-hide">
                        {results.map((result) => (
                            <li
                                key={result.place_id}
                                onClick={() => handleSelect(result)}
                                className="cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-white/50 dark:hover:bg-gray-700/50 transition-colors duration-150 border-b border-gray-100/50 dark:border-gray-700/50 last:border-0"
                            >
                                <div className="flex items-start">
                                    <span className="flex-shrink-0 pt-0.5">
                                        <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </span>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {result.display_name.split(',')[0]}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">
                                            {result.display_name.split(',').slice(1).join(',')}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default SearchBar;
